import { errorOperationMap } from "app/pages/sales-intelligence/utils/errors/context";
import { isSiError } from "app/pages/sales-intelligence/utils/errors/types";
import type { ResponseError } from "app/services/fetchService";
import { ABORTED_CODE } from "app/services/fetchService";

const isEmptySearchResultsError = (error: unknown): boolean =>
    isSiError(error) && error.context.operation === errorOperationMap.emptySearchResults;

// Some queries might fail with 401 errors due to temporary infra issues & they should be retried.
export const retryQueryOnUnauthorizedError = (failureCount: number, error: unknown): boolean => {
    return failureCount < 2 && [401, 403].includes((error as ResponseError)?.status);
};

export const retryQueryOnInternalServerError = (failureCount: number, error: unknown): boolean => {
    return failureCount < 1 && (error as ResponseError)?.status === 500;
};

// Retries on transient 401/403 infra blips AND on backend-cancelled 204→null responses
export const retrySearchResultsQuery = (failureCount: number, error: unknown): boolean => {
    return retryQueryOnUnauthorizedError(failureCount, error) || (failureCount < 2 && isEmptySearchResultsError(error));
};

// Exponential backoff with additive jitter for SI search-results retries. The jitter
// prevents the four concurrent search queries from recomputing the same delay and
// hammering the backend in lockstep on the retry attempt.
const SEARCH_RESULTS_BASE_DELAY_MS = 1000;
const SEARCH_RESULTS_MAX_DELAY_MS = 5000;
const SEARCH_RESULTS_JITTER_MAX_MS = 250;

export const retryDelayForSearchResults = (failureCount: number): number => {
    const exponential = Math.min(SEARCH_RESULTS_BASE_DELAY_MS * 2 ** failureCount, SEARCH_RESULTS_MAX_DELAY_MS);
    const jitter = Math.random() * SEARCH_RESULTS_JITTER_MAX_MS;

    return exponential + jitter;
};

const TIMEOUT_ERROR_STATUSES: ReadonlySet<number> = new Set([502, 504]);

export const isTimeoutError = (error: unknown): boolean => {
    return TIMEOUT_ERROR_STATUSES.has((error as ResponseError)?.status);
};

// 403 is added to handled errors (not timeout errors) because in our system
// it falls through from the server when there is a timeout issue / DB is too busy.
// We retry 403 (see retryQueryOnUnauthorizedError) as the issue MIGHT be resolved after a retry.
const HANDLED_ERROR_STATUS: ReadonlySet<number> = new Set([403, ...TIMEOUT_ERROR_STATUSES, ABORTED_CODE]);

export const throwOnUnhandledError = (error: unknown): boolean => {
    const isHandledError = HANDLED_ERROR_STATUS.has((error as ResponseError)?.status);

    return !isHandledError;
};

export const throwOnUnhandledSearchError = (error: unknown): boolean => {
    return throwOnUnhandledError(error) && !isEmptySearchResultsError(error);
};
